import type { CommandStatus } from '@gk/contracts'

/** What `api.sync_health(p_org, p_site)` answers (supabase/migrations/20261115100200_sync_health.sql, its latest version in 20261116100200_sync_health_fixes.sql; pgTAP 46
 *  pins the shape): change them together. The page "Santé de la synchro" reads it online, for the people who hold audit.read on the site; nothing of it is kept on the
 *  device. The server cuts three lists (the devices at 200, the diagnostics at 50, the failed jobs at 20) and says how many there are, so the page can say so. */
export interface CommandWindow {
  total: number
  by_status: Partial<Record<CommandStatus, number>>
  by_name: Array<{ name: string; status: CommandStatus; n: number }>
  /** from the moment a command happened (the device's clock corrected by its offset) to its arrival at the server; null when the window has no command */
  latency_ms: { p50: number | null; p90: number | null; p99: number | null; max: number | null }
}
export type SkewBucket = 'lt_2s' | 'lt_1m' | 'lt_1h' | 'ge_1h' | 'unknown'
export const SKEW_BUCKETS: readonly SkewBucket[] = ['lt_2s', 'lt_1m', 'lt_1h', 'ge_1h', 'unknown']
export interface HealthDevice {
  device_id: string; kind: 'personal' | 'kiosk'; label: string | null; person_id: string | null; display_name: string | null
  last_seen_at: string | null; outbox_depth: number | null; oldest_pending_at: string | null; storage_persisted: boolean | null; standalone: boolean | null
  app_version: string | null; clock_offset_ms: number | null; pending_over_24h: boolean
}
export interface HealthDiagnostic { id: string; at: string; received_at: string; code: string; message: string; app_version: string | null; device_id: string; display_name: string | null }
export interface CronFailure { job: string | null; status: string; start_time: string; end_time: string | null; message: string }
export interface SyncHealth {
  generated_at: string
  commands: { h24: CommandWindow; d7: CommandWindow }
  clock_skew: Array<{ bucket: SkewBucket; n: number }>
  app_versions: Array<{ app_version: string | null; devices: number }>
  /** at most 200: first the ones with work waiting for more than 24 h, then the shared workstations, then the last seen (shown the last seen first) */
  devices: HealthDevice[]
  /** the site's unrevoked devices, listed or not */
  devices_total: number
  /** the 50 newest of the club's kept period, how many there are in it, that period in days (the club's retention policy), and the week's by code */
  diagnostics: { last: HealthDiagnostic[]; total: number; kept_days: number; by_code_d7: Array<{ code: string; n: number }> }
  /** the 20 newest failed runs of the last 7 days, and how many there were */
  cron_failures: CronFailure[]
  cron_failures_total: number
  backup: { expected: boolean; last_ok_at: string | null; age_hours: number | null; last_failure_at: string | null }
}

/** After this many hours without a good backup the dead-man check raises a review item (M10); the page says the same. */
export const BACKUP_OVERDUE_HOURS = 36

/** The statuses that mean the server did not take the command as it was, in the order the page lists them. */
export const PROBLEM_STATUSES: readonly CommandStatus[] = ['error', 'rejected', 'conflict', 'blocked', 'transient']
/** All statuses a command can end in, for the pills (each shown only when it has a count). */
export const OUTCOME_ORDER: readonly CommandStatus[] = ['applied', 'applied_with_exception', 'rejected', 'conflict', 'error', 'blocked', 'transient', 'discarded']

export const statusCount = (w: CommandWindow, s: CommandStatus): number => w.by_status[s] ?? 0
/** The commands of the window the server did not take as they were (most first, then by name): what somebody should look at. */
export function problemRows(w: CommandWindow): CommandWindow['by_name'] {
  return w.by_name.filter((r) => PROBLEM_STATUSES.includes(r.status)).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name) || a.status.localeCompare(b.status))
}
/** Share of the window's commands the server took (applied, with or without an exception), in percent rounded down; null for an empty window. */
export function takenPercent(w: CommandWindow): number | null {
  if (!w.total) return null
  return Math.floor((100 * (statusCount(w, 'applied') + statusCount(w, 'applied_with_exception'))) / w.total)
}

/** A duration in the unit a person reads at a glance (the page shows it with `syncHealth.dur.<unit>`). Negative offsets keep their sign. */
export function durationParts(ms: number): { unit: 'ms' | 's' | 'min' | 'h' | 'd'; n: number } {
  const a = Math.abs(ms), sign = ms < 0 ? -1 : 1
  if (a < 1000) return { unit: 'ms', n: sign * Math.round(a) }
  if (a < 60_000) return { unit: 's', n: sign * Math.round(a / 100) / 10 }
  if (a < 3_600_000) return { unit: 'min', n: sign * Math.round(a / 60_000) }
  if (a < 86_400_000) return { unit: 'h', n: sign * Math.round(a / 360_000) / 10 }
  return { unit: 'd', n: sign * Math.round(a / 8_640_000) / 10 }
}

/** The name a device is shown by: its person, else a workstation's label, else the start of its id. */
export const deviceName = (d: Pick<HealthDevice, 'display_name' | 'label' | 'device_id'>): string => d.display_name ?? d.label ?? d.device_id.slice(0, 8)

/** This browser's device first, then the ones with work waiting for more than 24 h, then the last seen first (the server's order). */
export function sortDevices(devs: HealthDevice[], mine: string): HealthDevice[] {
  return devs.map((d, i) => ({ d, i })).sort((a, b) => Number(b.d.device_id === mine) - Number(a.d.device_id === mine) || Number(b.d.pending_over_24h) - Number(a.d.pending_over_24h) || a.i - b.i).map((x) => x.d)
}

/** How many devices and diagnostics the page shows before "show all" (a club's list grows with every browser a person ever used). */
export const DEVICES_SHOWN = 8
export const DIAGNOSTICS_SHOWN = 10
/** The devices shown folded: the first DEVICES_SHOWN of the sorted list, and every device with work waiting for more than 24 h whatever its place. */
export const foldDevices = (sorted: HealthDevice[], expanded: boolean): HealthDevice[] => (expanded ? sorted : sorted.filter((d, i) => i < DEVICES_SHOWN || d.pending_over_24h))

/** The width of a bar, in percent of the largest count (at least 2 % when the count is not zero, so a small count stays visible). */
export function barPercent(n: number, max: number): number {
  if (!n || !max) return 0
  return Math.max(2, Math.round((100 * n) / max))
}

/** When the server cut a list: how many are shown of how many (the page says it); null when nothing was cut. */
export function listCut(shown: number, total: number | null | undefined): { shown: number; count: number } | null {
  return typeof total === 'number' && total > shown ? { shown, count: total } : null
}

export const backupOverdue = (b: SyncHealth['backup']): boolean => b.expected && (b.last_ok_at === null || (b.age_hours ?? 0) > BACKUP_OVERDUE_HOURS)
